import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Tag, ArrowRight } from 'lucide-react';
import wpPosts from '../data/blogPosts.json';
import { getGeneratedArticles } from '../data/blogLoader';

function normalize(p) {
  return {
    slug: p.slug,
    title: p.title,
    date: p.date,
    excerpt: p.excerpt,
    categories: p.categories || [],
    tags: p.tags || [],
    featured: p.featured || '',
    wordCount: p.wordCount || 0,
  };
}

export default function BlogListPage() {
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('All');

  const posts = useMemo(() => {
    const gen = getGeneratedArticles();
    const all = [...gen, ...wpPosts].map(normalize);
    // Dedupe by slug (generated wins)
    const seen = new Set();
    return all.filter(p => (seen.has(p.slug) ? false : (seen.add(p.slug), true)))
              .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  }, []);

  const categories = useMemo(() => {
    const s = new Set(['All']);
    posts.forEach(p => (p.categories || []).forEach(c => s.add(c)));
    return Array.from(s);
  }, [posts]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return posts.filter(p => {
      if (cat !== 'All' && !(p.categories || []).includes(cat)) return false;
      if (!qq) return true;
      return p.title.toLowerCase().includes(qq) || (p.excerpt || '').toLowerCase().includes(qq);
    });
  }, [posts, q, cat]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="blog-list-page">
      {/* Hero */}
      <div className="rounded-2xl bg-gradient-to-r from-blue-700 to-indigo-700 text-white px-8 py-10 mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Calculator Guides &amp; Tips</h1>
        <p className="text-blue-100 max-w-2xl">In-depth articles on mortgages, loans, fitness, math and more — updated for 2026.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search articles..."
            className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-300 rounded-lg outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            data-testid="blog-search"
          />
        </div>
        <select
          value={cat}
          onChange={e => setCat(e.target.value)}
          className="px-3 py-2.5 bg-white border border-slate-300 rounded-lg outline-none focus:border-blue-500"
          data-testid="blog-category-filter"
        >
          {categories.map(c => <option key={c}>{c}</option>)}
        </select>
      </div>

      <div className="text-sm text-slate-500 mb-4">Showing {filtered.length} of {posts.length} articles</div>

      {/* Post grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(p => (
          <Link
            key={p.slug}
            to={`/blog/${p.slug}`}
            className="group bg-white rounded-xl border border-slate-200 hover:border-blue-400 hover:shadow-lg transition-all overflow-hidden flex flex-col"
            data-testid={`blog-card-${p.slug}`}
          >
            {p.featured ? (
              <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                <img src={p.featured} alt={p.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
            ) : (
              <div className="aspect-[16/9] bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center">
                <span className="text-4xl font-bold text-blue-300">{p.title.charAt(0)}</span>
              </div>
            )}
            <div className="p-5 flex-1 flex flex-col">
              {p.categories && p.categories[0] && (
                <div className="flex items-center gap-1 text-xs text-blue-600 font-semibold mb-2">
                  <Tag className="w-3 h-3" /> {p.categories[0]}
                </div>
              )}
              <h3 className="font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-blue-700">{p.title}</h3>
              <p className="text-sm text-slate-600 line-clamp-3 flex-1">{p.excerpt}</p>
              <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {(p.date || '').slice(0, 10)}</span>
                <span className="text-blue-600 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight className="w-3 h-3" /></span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16 text-slate-500">No articles match your filters.</div>
      )}
    </div>
  );
}
